import { Grid, IconButton, Menu, MenuItem } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ButtonMultiAction from "@saleor/components/ButtonMultiAction";
import CardTitle from "@saleor/components/CardTitle";
import { Hr } from "@saleor/components/Hr";
import Skeleton from "@saleor/components/Skeleton";
import useNotifier from "@saleor/hooks/useNotifier";
import RemittanceStatusOverride from "@saleor/remittanceRunner/components/RemittanceStatusOverride";
import {
  isOrderManuallyAdjusted,
  orderForManualAdjustment
} from "@saleor/remittanceRunner/utils/handlers";
import {
  RemittanceManualAdjustmentStatusEnum,
  RemittanceStatusEnum,
  SmopOrderStatusEnum
} from "@saleor/types/globalTypes";
import { canManageOverrideRemittance } from "@saleor/utils/handlers/userHandler";
import { getPaymentChannelCode } from "@saleor/utils/paymentChannelCode/paymentChannelCode";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { maybe, transformPaymentStatus } from "../../../misc";
import RemittanceManualAdjustDialog from "../../../remittanceRunner/components/RemittanceManualAdjustDialog";
import { OrderDetails_order } from "../../types/OrderDetails";

const useStyles = makeStyles(
  theme => ({
    btnManualAdjust: {
      "& div": {
        fontWeight: 600
      },
      "& span": {
        color: "#E26139"
      },
      border: "1px solid #E26139",
      float: "right"
    },
    listItemBtn: {
      textTransform: "none"
    },
    menuItem: {
      marginLeft: 2,
      marginRight: 2,
      padding: 0
    },
    root: {
      ...theme.typography.body1,
      lineHeight: 1.9,
      width: "100%"
    },
    textBolder: {
      fontWeight: 600
    },
    textRed: {
      color: "#E26139"
    },
    textRight: {
      textAlign: "right"
    }
  }),
  { name: "OrderPayment" }
);

interface OrderPaymentProps {
  canManualAdjust: boolean;
  order: OrderDetails_order;
  orderInfoComponent?: React.ReactNode;
  refetchOrder: (variables?: any) => Promise<any>;
}

const OrderPayment: React.FC<OrderPaymentProps> = props => {
  const { canManualAdjust, order, orderInfoComponent, refetchOrder } = props;
  const classes = useStyles(props);
  const intl = useIntl();
  const notify = useNotifier();

  const [manualAdjustModalOpen, setManualAdjustModalOpen] = useState(false);
  const [statusOverrideModalOpen, setStatusOverrideModalOpen] = useState(false);
  const [openRemittanceAction, setOpenRemittanceAction] = useState(false);
  const [width, setWidth] = React.useState(0);
  const [length, setLength] = React.useState(0);
  const [height, setHeight] = React.useState(0);
  const [
    expandManualAdjustments,
    setExpandManualAdjustments
  ] = React.useState<null | HTMLElement>(null);

  React.useEffect(() => {
    const weight = parseFloat(order?.weight?.value);
    const calculatedValue = Math.pow(weight * 3500, 1 / 3);

    setWidth(Number(calculatedValue.toFixed(2)));
    setLength(Number(calculatedValue.toFixed(2)));
    setHeight(Number(calculatedValue.toFixed(2)));
  }, [order?.weight]);

  const formatter = new Intl.NumberFormat("en-PH", {
    currency: "PHP",
    currencyDisplay: "symbol",
    minimumFractionDigits: 2,
    style: "currency"
  });

  const payment = transformPaymentStatus(
    maybe(() => order.payments[0].chargeStatus),
    intl
  );

  const shippingLabel = orderData => {
    const orderLength = orderData.subOrders.length;
    let cnt = 0;
    let sellerText = "";

    for (let i = 0; i < orderLength; i++) {
      if (orderData.subOrders[i].smopStatus !== "CANCELED") {
        cnt++;
      }
    }

    if (cnt > 1) {
      sellerText = " Sellers";
    } else {
      sellerText = " Seller";
    }

    return cnt + sellerText;
  };

  const manualAdjustClick = () => {
    setManualAdjustModalOpen(true);
  };

  const closeManualAdjustModal = () => {
    setManualAdjustModalOpen(false);
    setOpenRemittanceAction(true);
  };

  const forManualAdjustment = (order: OrderDetails_order) =>
    orderForManualAdjustment(order);

  const amountLabel = (adjustedAmount: number) => {
    if (adjustedAmount < 0) {
      return "Amount Deducted";
    } else {
      return "Amount Added";
    }
  };

  const handleExpandManualAdjustments = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setExpandManualAdjustments(event.currentTarget);
  };

  const handleCloseManualAdjustments = () => {
    setExpandManualAdjustments(null);
  };

  const statusOverrideClick = () => {
    if (
      order?.remittanceLine?.remittance?.status ===
      RemittanceStatusEnum.REMITTED
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Order is already Remitted."
        })
      });
    } else if (
      order?.remittanceLine?.remittance?.status ===
      RemittanceStatusEnum.FOR_BANK_CONFIRMATION
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Order is already For Confirmation."
        })
      });
    } else {
      setStatusOverrideModalOpen(true);
    }
  };

  const closeStatusOverrideModal = () => {
    setStatusOverrideModalOpen(false);
    setOpenRemittanceAction(true);
  };

  const disableStatusOverrideAction = () =>
    order?.smopStatus !== SmopOrderStatusEnum.COMPLETED ||
    order?.remittanceManualAdjustmentStatus ===
      RemittanceManualAdjustmentStatusEnum.FOR_ADJUSTMENT ||
    order?.remittanceLine?.remittance?.status ===
      RemittanceStatusEnum.REMITTED ||
    order?.remittanceLine?.remittance?.status ===
      RemittanceStatusEnum.FOR_BANK_CONFIRMATION;

  const userCanOverrideRemittance = canManageOverrideRemittance();
  const remittanceAdjustActions = [
    {
      action: () => manualAdjustClick(),
      disabled: false,
      label: "Remittance Amount"
    },
    ...(userCanOverrideRemittance
      ? [
          {
            action: () => statusOverrideClick(),
            disabled: disableStatusOverrideAction(),
            label: "Status Override"
          }
        ]
      : [])
  ];

  return (
    <>
      <Card>
        <CardTitle
          title="Order Info & Payment"
          toolbar={
            !manualAdjustModalOpen &&
            !statusOverrideModalOpen &&
            canManualAdjust &&
            forManualAdjustment(order) && (
              <ButtonMultiAction
                btnClassStyle={classes.btnManualAdjust}
                itemBtnClassStyle={classes.listItemBtn}
                hideDivider={true}
                actions={remittanceAdjustActions}
                label="Remittance Adjustment"
                placement={"bottom-end"}
                forceVisibility={openRemittanceAction}
              />
            )
          }
        />
        <CardContent>
          {/* Order Info */}
          <div className={classes.root}>{orderInfoComponent}</div>
        </CardContent>
        <Hr />
        <CardContent>
          <table className={classes.root}>
            <tbody>
              <tr>
                <td>
                  <span>
                    <b>Length</b>
                  </span>
                </td>
                <td className={classes.textRight}>{length} CM</td>
              </tr>
              <tr>
                <td>
                  <span>
                    <b>Width</b>
                  </span>
                </td>
                <td className={classes.textRight}>{width} CM</td>
              </tr>
              <tr>
                <td>
                  <span>
                    <b>Height</b>
                  </span>
                </td>
                <td className={classes.textRight}>{height} CM</td>
              </tr>
              <tr>
                <td>
                  <span>
                    <b>Dimensional Weight</b>
                  </span>
                </td>
                <td className={classes.textRight}>
                  {order?.weight?.value} {order?.weight?.unit}
                </td>
              </tr>
              <Hr />

              <tr>
                <td>
                  {maybe(() => order.subOrders) === undefined ? (
                    <Skeleton />
                  ) : (
                    <span>
                      <b>Sub-total ({shippingLabel(order)})</b>
                    </span>
                  )}
                </td>

                <td className={classes.textRight}>
                  {maybe(() => order.subtotal.gross) === undefined ? (
                    <Skeleton />
                  ) : (
                    <span>{formatter.format(order.subtotal.gross.amount)}</span>
                  )}
                </td>
              </tr>
              <tr>
                <td>
                  <span>
                    <b>Delivery Fee</b>
                  </span>
                </td>
                <td className={classes.textRight}>
                  {maybe(() => order.shippingPrice.gross) === undefined ? (
                    <Skeleton />
                  ) : (
                    <span>
                      {formatter.format(order.shippingPrice.gross.amount)}
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <td>
                  <span>
                    <b>Voucher Discount</b>
                  </span>{" "}
                  {order && order.voucher ? (
                    <span>
                      <b>{order.voucher.code}</b>
                    </span>
                  ) : null}
                </td>
                <td className={classes.textRight}>
                  {order && order.discount ? (
                    <>
                      <span>-{formatter.format(order.discount.amount)}</span>
                    </>
                  ) : (
                    <Skeleton />
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </CardContent>
        <Hr />
        <CardContent>
          <table className={classes.root}>
            <tbody>
              {maybe(() => isOrderManuallyAdjusted(order)) ? (
                <>
                  <tr>
                    <td>
                      <b>
                        <FormattedMessage
                          defaultMessage="Original Total Amount"
                          description="Original Total Amount"
                        />
                      </b>
                    </td>
                    <td
                      className={`${classes.textRight} ${classes.textBolder}`}
                    >
                      {maybe(() => order.total.gross) === undefined ? (
                        <Skeleton />
                      ) : (
                        <span>
                          {formatter.format(order.total.gross.amount)}
                        </span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>Amount Deducted/Added</b>
                    </td>
                    <td
                      className={
                        order?.remittanceManualAdjustmentAmount < 0
                          ? `${classes.textRight} ${classes.textRed}`
                          : `${classes.textRight}`
                      }
                    >
                      {maybe(() => order?.remittanceManualAdjustmentAmount) ===
                      undefined ? (
                        <Skeleton />
                      ) : (
                        <span>
                          {formatter.format(
                            order?.remittanceManualAdjustmentAmount
                          )}

                          {order.remittanceManualAdjustments.length > 1 ? (
                            <>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={handleExpandManualAdjustments}
                              >
                                <ExpandMoreIcon fontSize="small" />
                              </IconButton>
                              <Menu
                                style={{ marginTop: 40 }}
                                id="menuRemittanceManualAdjustments"
                                anchorEl={expandManualAdjustments}
                                keepMounted
                                open={Boolean(expandManualAdjustments)}
                                onClose={handleCloseManualAdjustments}
                                anchorOrigin={{
                                  horizontal: "center",
                                  vertical: "bottom"
                                }}
                                transformOrigin={{
                                  horizontal: "center",
                                  vertical: "top"
                                }}
                              >
                                {order.remittanceManualAdjustments.map(
                                  adjustment => (
                                    <MenuItem
                                      divider
                                      className={classes.menuItem}
                                    >
                                      <Grid
                                        container
                                        spacing={10}
                                        justify="space-evenly"
                                      >
                                        <Grid item xs={6}>
                                          {amountLabel(
                                            adjustment.adjustedAmount
                                          )}
                                        </Grid>
                                        <Grid item xs={6}>
                                          {maybe(() => adjustment) ===
                                          undefined ? (
                                            <Skeleton />
                                          ) : (
                                            <div
                                              className={
                                                adjustment.adjustedAmount < 0
                                                  ? `${classes.textRight} ${classes.textRed}`
                                                  : `${classes.textRight}`
                                              }
                                            >
                                              {adjustment.adjustedAmount > 0
                                                ? "+" +
                                                  formatter.format(
                                                    adjustment.adjustedAmount
                                                  )
                                                : formatter.format(
                                                    adjustment.adjustedAmount
                                                  )}
                                            </div>
                                          )}
                                        </Grid>
                                      </Grid>
                                    </MenuItem>
                                  )
                                )}
                              </Menu>
                            </>
                          ) : null}
                        </span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>
                        <FormattedMessage
                          defaultMessage="Adjusted Total Amount"
                          description="Adjusted Total Amount"
                        />
                      </b>
                    </td>
                    <td
                      className={`${classes.textRight} ${classes.textBolder}`}
                    >
                      {maybe(
                        () => order.remittanceManualAdjustmentTotalAmount
                      ) === undefined ? (
                        <Skeleton />
                      ) : (
                        <span>
                          {formatter.format(
                            order.remittanceManualAdjustmentTotalAmount
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td>
                    <b>
                      <FormattedMessage
                        defaultMessage="Total"
                        description="order total price"
                      />
                    </b>
                  </td>
                  <td className={classes.textRight}>
                    {maybe(() => order.total.gross) === undefined ? (
                      <Skeleton />
                    ) : (
                      <span>{formatter.format(order.total.gross.amount)}</span>
                    )}
                  </td>
                </tr>
              )}

              <tr>
                <td>
                  <b>
                    <FormattedMessage
                      defaultMessage="Payment Mode"
                      description="order payment"
                    />
                  </b>
                </td>
                <td className={classes.textRight}>
                  {maybe(() => order.payments[0].paymentMethodType) ===
                  undefined ? (
                    <Skeleton />
                  ) : (
                    <span>{getPaymentChannelCode(order)}</span>
                  )}
                </td>
              </tr>
              <tr>
                <td>
                  <b>
                    <FormattedMessage
                      defaultMessage="Payment Status"
                      description="order payment"
                    />
                  </b>
                </td>
                <td className={classes.textRight}>
                  {maybe(() => order.payments[0].chargeStatus) === undefined ? (
                    <Skeleton />
                  ) : (
                    <span>{payment.localized.toUpperCase()}</span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      <RemittanceManualAdjustDialog
        open={manualAdjustModalOpen}
        onClose={closeManualAdjustModal}
        order={order}
        refetchOrder={refetchOrder}
      />

      <RemittanceStatusOverride
        open={statusOverrideModalOpen}
        onClose={closeStatusOverrideModal}
        order={order}
        refetchOrder={refetchOrder}
      />
    </>
  );
};
OrderPayment.displayName = "OrderPayment";
export default OrderPayment;
